import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import axiosInstance from '../utils/axiosConfig';

/**
 * Multi-select for a preceptor's ranks, backed by db.ranks (Manage Ranks).
 * Add/remove as many ranks as the preceptor should serve — replaces the old
 * single "pharmd/spep/both" dropdown.
 *
 * Fetches the rank list itself so it can be dropped into any form; pass
 * `ranks`/`ranksLoading` instead if the parent already has them loaded.
 */
const RankMultiSelect = ({
  value = [],
  onChange,
  ranks: ranksProp,
  ranksLoading: ranksLoadingProp,
  required = false,
  isDisabled = false,
  placeholder = 'Select rank(s)…'
}) => {
  const [fetchedRanks, setFetchedRanks] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);

  const managed = ranksProp !== undefined;
  const ranks = managed ? ranksProp : fetchedRanks;
  const loading = managed ? ranksLoadingProp : fetchLoading;

  useEffect(() => {
    if (managed) return;
    let cancelled = false;
    setFetchLoading(true);
    axiosInstance.get('/ranks/ranks')
      .then((res) => { if (!cancelled) setFetchedRanks(Array.isArray(res.data) ? res.data : []); })
      .catch((err) => console.error('Failed to load ranks:', err))
      .finally(() => { if (!cancelled) setFetchLoading(false); });
    return () => { cancelled = true; };
  }, [managed]);

  const options = (ranks || []).map((rank) => ({ value: rank._id, label: rank.name }));
  const selected = options.filter((opt) => value.includes(opt.value));

  return (
    <Select
      isMulti
      closeMenuOnSelect={false}
      isDisabled={isDisabled || loading}
      value={selected}
      onChange={(chosen) => onChange((chosen || []).map((opt) => opt.value))}
      options={options}
      placeholder={loading ? 'Loading ranks...' : placeholder}
      classNamePrefix="rank-select"
      // Render the open menu into a body-level portal so it isn't clipped or
      // squashed by an ancestor with overflow:hidden/auto — e.g. the Manage
      // Users "modify user" modal — which otherwise makes it look broken
      // compared to the same field on a user's own (non-modal) account page.
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{
        container: (base) => ({ ...base, width: '100%' }),
        control: (base) => ({
          ...base,
          minHeight: 44,
          fontSize: '0.95rem',
          borderColor: required && selected.length === 0 ? '#fca5a5' : base.borderColor
        }),
        placeholder: (base) => ({ ...base, color: '#94a3b8' }),
        menuPortal: (base) => ({ ...base, zIndex: 10050 }),
      }}
    />
  );
};

export default RankMultiSelect;
